import { Link } from 'react-router-dom'
import { lighten } from '@mui/material/styles'
import { useDropzone } from 'react-dropzone'
import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Button,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { UUID } from 'crypto'
import { useFundingNeedDocs } from '@/utils/hooks/fundingNeeds'
import { useSSEProgress } from '@/pages/onboarding/hooks/useSSEProgress'
import {
  UPLOAD_ACCEPT,
  ALLOWED_FILE_TYPES_LABEL,
  describeRejectionReason,
} from '@/utils/uploadFileTypes'

const MAX_FILES = Number(import.meta.env.VITE_MAX_FILES_PER_UPLOAD) || 15

export default function UploadFundingNeedFile({
  orgUuid,
  token,
  fundingNeedId,
  onPendingChange,
}: {
  orgUuid: UUID
  token: string
  fundingNeedId: number
  // Lets a parent form (which may have its own, separate "Save" action)
  // know files are staged here but not yet uploaded, so it isn't saved
  // over without warning. See #1465.
  onPendingChange?: (hasPending: boolean) => void
}) {
  const [files, setFiles] = useState<File[]>([])
  const [uploaded, setUploaded] = useState<File[] | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dropError, setDropError] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Reads `files` from the closure rather than a setFiles(prev => ...) updater,
      // so setDropError below isn't called from inside a state updater (updaters must
      // be pure and can otherwise fire twice under StrictMode).
      const newTotalCount = files.length + acceptedFiles.length
      if (newTotalCount > MAX_FILES) {
        setDropError(
          `Maximum files that can be uploaded at one time is ${MAX_FILES}`,
        )
        return
      }
      setDropError(
        rejectedFiles.length > 0
          ? `${rejectedFiles.map((r) => r.file.name).join(', ')} - ${describeRejectionReason(rejectedFiles[0])} Allowed types: ${ALLOWED_FILE_TYPES_LABEL}.`
          : null,
      )
      if (acceptedFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...acceptedFiles])
      }
    },
    accept: UPLOAD_ACCEPT,
  })

  const {
    isLoading,
    uploadFundingNeedDocs,
    refetch: refetchDocs,
    isError: docsError,
  } = useFundingNeedDocs(orgUuid, token as string, fundingNeedId)

  const uploadProgress = useSSEProgress({
    type: 'upload',
    jobId,
    token,
    onComplete: () => {
      setUploaded(pendingFiles)
      setFiles([])
      setPendingFiles([])
      setUploading(false)
      setJobId(null)
      setDropError(null)
      refetchDocs()
    },
    // Ingestion failures publish the worker's raw exception text (app/worker.py's
    // _handle_file_upload) — none of it is curated for end users, so always show
    // a generic message rather than passing it through.
    onError: () => {
      setUploadError('Error uploading files')
      setUploading(false)
      setJobId(null)
    },
  })

  // A ref rather than an effect dependency: when this widget is rendered
  // inline in a list (FundingNeedsList), the parent passes a new
  // onPendingChange closure every render. Depending on it directly would
  // re-run the effect below every render, and since the cleanup fires with
  // the previous (stale) closure before the new one runs, that toggles the
  // parent's state false-then-true forever (#1465).
  const onPendingChangeRef = useRef(onPendingChange)
  useEffect(() => {
    onPendingChangeRef.current = onPendingChange
  })

  // `files` covers both "staged, not yet uploaded" and "upload in flight" —
  // it's only cleared once the upload completes (see onComplete above). The
  // cleanup clears the parent's flag on unmount (e.g. switching upload tabs),
  // since staged files are lost with this component and can no longer block.
  useEffect(() => {
    onPendingChangeRef.current?.(files.length > 0)
    return () => onPendingChangeRef.current?.(false)
  }, [files.length])

  async function handleUpload() {
    setUploading(true)
    setUploaded(null)
    setUploadError(null)
    setDropError(null)

    if (!fundingNeedId || isNaN(fundingNeedId)) {
      setUploadError('Invalid Funding Need ID')
      setUploading(false)
      return
    }

    try {
      const response = await uploadFundingNeedDocs.mutateAsync({
        token,
        files,
        orgUuid,
        fundingNeedId,
      })
      setPendingFiles(files)
      setJobId(response.job_id)
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Error uploading files',
      )
      setUploading(false)
    }
  }

  function handleRemoveFile(index: number) {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
    setDropError(null)
    setUploadError(null)
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (docsError) {
    return <Alert severity="error">Error loading documents</Alert>
  }

  return (
    <>
      <Box sx={{ p: 3, width: isMobile ? '100%' : '80%', mx: 'auto', mb: 3 }}>
        <Typography variant="h5" mb={2}>
          Upload Files for this Funding Request
        </Typography>
        <Typography variant="body2" fontWeight={500} mb={2}>
          Files uploaded here are only used for this funding request — The
          content is not added to your organisation's general profile or any
          other funding request. <br /> If you want to add general content that
          is relevant to all funding requests, you can upload it in your
          organisation's profile <Link to="/profile/documents">here</Link>
        </Typography>
        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            textAlign: 'center',
            border: '2px dashed #ccc',
            borderRadius: 2,
            backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
            cursor: 'pointer',
            boxShadow: 'none',
          }}
        >
          <input {...getInputProps()} />
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UploadFileIcon sx={{ mr: 1 }} />
            {isMobile
              ? 'Tap here to upload'
              : 'Drag and drop files here or click to upload'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Max file size: 25MB. Max files per upload: {MAX_FILES}. Allowed
            types: {ALLOWED_FILE_TYPES_LABEL}
          </Typography>
        </Paper>

        {dropError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            onClose={() => setDropError(null)}
          >
            <Box
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}
            >
              {dropError}
            </Box>
          </Alert>
        )}

        {files.length > 0 && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              mt: 3,
              justifyContent: 'center',
              // Group the staged files, their remove controls and the Upload CTA
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              // Same derivation as the staged-upload caption elsewhere in this
              // flow, so the two read as one system rather than two tints.
              bgcolor: lighten(theme.palette.warning.light, 0.9),
              border: `1px solid ${lighten(theme.palette.warning.light, 0.7)}`,
            })}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Files ready to upload ({files.length})
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontStyle: 'italic', color: 'text.secondary' }}
            >
              Click the Upload button below to continue, or remove the file(s)
              from the list to cancel the upload
            </Typography>
            <List>
              {files.map((file, index) => (
                <ListItem
                  key={index}
                  disableGutters={isMobile}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                      aria-label={`Remove ${file.name} file from upload list`}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={file.name}
                    slotProps={{
                      primary: { sx: { overflowWrap: 'anywhere' } },
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Button
              variant="contained"
              disableRipple
              onClick={handleUpload}
              disabled={uploading}
              sx={{
                mt: 2,
                width: '100%',
                bgcolor: '#1C3045',
                color: '#fff',
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: 3,
                gap: 2,
                maxWidth: 400,
                mx: 'auto',
                fontSize: '16px',
                '&:hover': {
                  bgcolor: '#16283a',
                  boxShadow: 'none',
                },
                '&:focus': {
                  outline: '2px solid #1C3045',
                  outlineOffset: '2px',
                },
                '&.Mui-disabled': {
                  bgcolor: '#1C3045',
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  <span>Uploading files... {uploadProgress.progress}%</span>
                </>
              ) : (
                `Upload ${files.length} file${files.length === 1 ? '' : 's'}`
              )}
            </Button>
          </Box>
        )}

        {uploaded && (
          <Alert severity="success" sx={{ mt: 3 }}>
            Files uploaded successfully!
          </Alert>
        )}
        {uploadError && (
          <Alert
            severity="error"
            sx={{ mt: 3 }}
            onClose={() => setUploadError(null)}
          >
            {uploadError}
          </Alert>
        )}
      </Box>
    </>
  )
}
